import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import {
  joinBoard,
  leaveBoard,
  sendBoardCursor,
  onBoardPresence,
  onBoardCursor,
  BoardMember,
} from '../../services/realtimeService';

const CURSOR_STALE_MS = 4000;
const CURSOR_THROTTLE_MS = 50;

export interface RemoteCursor {
  userId: string;
  displayName?: string;
  avatarUrl?: string;
  x: number;
  y: number;
  ts: number;
}

const COLORS = ['#ef4444','#f59e0b','#10b981','#3b82f6','#8b5cf6','#ec4899','#14b8a6','#f97316'];
export function colorForUser(userId: string): string {
  let h = 0;
  for (const c of userId) h = (h * 33 + c.charCodeAt(0)) % COLORS.length;
  return COLORS[h];
}

export default function useBoardPresence(
  boardId: string | null | undefined,
  currentUserId: string | undefined,
  displayName: string | undefined,
  avatarUrl: string | undefined,
) {
  const [members, setMembers] = useState<BoardMember[]>([]);
  const [cursors, setCursors] = useState<Record<string, RemoteCursor>>({});
  const lastEmitRef = useRef(0);

  // Batch cursor updates via rAF — max 1 state update per frame
  const pendingCursorsRef = useRef<Record<string, RemoteCursor>>({});
  const rafCursorRef = useRef(0);

  // Stable refs for effect dependencies to avoid join/leave thrashing
  const displayNameRef = useRef(displayName);
  const avatarUrlRef = useRef(avatarUrl);
  displayNameRef.current = displayName;
  avatarUrlRef.current = avatarUrl;

  useEffect(() => {
    if (!boardId) return;
    joinBoard(boardId, displayNameRef.current, avatarUrlRef.current);
    const offPresence = onBoardPresence((p) => {
      if (p.boardId === boardId) setMembers(p.members);
    });
    const offCursor = onBoardCursor((p) => {
      if (p.boardId !== boardId || p.fromUserId === currentUserId) return;
      pendingCursorsRef.current[p.fromUserId] = {
        userId: p.fromUserId,
        displayName: p.displayName,
        avatarUrl: p.avatarUrl,
        x: p.x,
        y: p.y,
        ts: Date.now(),
      };
      if (!rafCursorRef.current) {
        rafCursorRef.current = requestAnimationFrame(() => {
          rafCursorRef.current = 0;
          const pending = pendingCursorsRef.current;
          pendingCursorsRef.current = {};
          if (Object.keys(pending).length === 0) return;
          setCursors((prev) => ({ ...prev, ...pending }));
        });
      }
    });
    return () => {
      leaveBoard(boardId);
      offPresence();
      offCursor();
      if (rafCursorRef.current) cancelAnimationFrame(rafCursorRef.current);
      setMembers([]);
      setCursors({});
    };
  }, [boardId, currentUserId]);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setCursors((prev) => {
        const next: typeof prev = {};
        let changed = false;
        for (const [k, v] of Object.entries(prev)) {
          if (now - v.ts < CURSOR_STALE_MS) {
            next[k] = v;
          } else {
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const emitCursor = useCallback(
    (worldX: number, worldY: number) => {
      if (!boardId) return;
      const now = Date.now();
      if (now - lastEmitRef.current < CURSOR_THROTTLE_MS) return;
      lastEmitRef.current = now;
      sendBoardCursor(boardId, worldX, worldY, displayNameRef.current, avatarUrlRef.current);
    },
    [boardId],
  );

  const otherMembers = useMemo(
    () => members.filter((m) => m.userId !== currentUserId),
    [members, currentUserId],
  );

  const remoteCursors = useMemo(() => Object.values(cursors), [cursors]);

  return { members: otherMembers, remoteCursors, emitCursor };
}
